
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Trash2, User, Calendar, Target } from 'lucide-react';
import { MissionParticipant } from '@/hooks/useAdminMissionParticipants';
import { imageUrlService } from '@/services/ImageUrlService';

interface MissionParticipantCardProps {
  participant: MissionParticipant;
  onRemove: (userMissionId: string) => void;
  isRemoving: boolean;
}

const MissionParticipantCard: React.FC<MissionParticipantCardProps> = ({
  participant,
  onRemove,
  isRemoving
}) => {
  const avatarUrl = participant.user.main_photo_url 
    ? imageUrlService.getImageUrl(participant.user.main_photo_url, 'avatar')
    : undefined;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-500/20 text-green-400';
      case 'ongoing': return 'bg-blue-500/20 text-blue-400';
      case 'expired': return 'bg-red-500/20 text-red-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed': return 'Concluída';
      case 'ongoing': return 'Em Andamento';
      case 'expired': return 'Expirada';
      default: return 'Desconhecido';
    }
  };

  return (
    <Card className="bg-black/20 backdrop-blur-sm border-white/10 hover:bg-white/5 transition-all">
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-start justify-between gap-4">
          {/* User Info */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Avatar className="h-12 w-12 ring-2 ring-white/10">
              <AvatarImage 
                src={avatarUrl || ''} 
                alt={participant.user.username}
                className="object-cover"
              />
              <AvatarFallback className="bg-luxury-purple/20 text-white">
                {participant.user.username[0]?.toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-medium truncate">
                @{participant.user.username}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="secondary" className={getStatusColor(participant.status)}>
                  {getStatusLabel(participant.status)}
                </Badge>
              </div>
            </div>
          </div>

          {/* Remove Button */}
          <Button
            variant="admin-outline"
            size="sm"
            onClick={() => onRemove(participant.id)}
            disabled={isRemoving}
            className="flex-shrink-0"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>

        {/* Progress Section */}
        <div className="mt-4 space-y-3">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Target className="h-3 w-3 text-white/60" />
                <span className="text-white/60 text-xs">Dia Atual</span>
              </div>
              <span className="text-white font-medium">{participant.current_day}/10</span>
            </div>
            
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <User className="h-3 w-3 text-white/60" />
                <span className="text-white/60 text-xs">Completos</span>
              </div>
              <span className="text-white font-medium">
                {participant.progress.completed_days}/{participant.progress.total_days}
              </span>
            </div>
            
            <div className="text-center col-span-2 sm:col-span-1">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Calendar className="h-3 w-3 text-white/60" />
                <span className="text-white/60 text-xs">Iniciado</span>
              </div>
              <span className="text-white font-medium text-xs">
                {new Date(participant.started_at).toLocaleDateString('pt-BR')}
              </span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-white/60">Progresso</span>
              <span className="text-white">{participant.progress.completion_percentage}%</span>
            </div>
            <Progress 
              value={participant.progress.completion_percentage} 
              className="h-2 bg-gray-800"
            />
          </div>

          {/* Completion Info */}
          {participant.status === 'completed' && participant.completed_at && (
            <div className="text-center p-2 bg-green-500/10 border border-green-500/20 rounded-lg">
              <span className="text-green-400 text-sm font-medium">
                Concluída em {new Date(participant.completed_at).toLocaleDateString('pt-BR')}
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default MissionParticipantCard;
